/**
 * usePendingChanges — Singleton store for optimistic entity-level change tracking.
 *
 * Provides:
 * 1. Optimistic pendingReviewIds (added BEFORE the API call, removed on failure)
 * 2. Proposed entity snapshots for Before→After card preview
 * 3. Cross-tab sync via MENU_DATA_UPDATED_EVENT
 * 4. Duplicate submission guard
 */

import { useCallback, useSyncExternalStore } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────

export interface ProposedChange {
  entityId: string;
  entityType: 'item' | 'category' | 'optionGroup';
  changeType: 'MODIFY' | 'DELETE' | 'ADD';
  /** The new (proposed) entity data snapshot */
  proposed: Record<string, unknown>;
  /** The previous (live) entity data snapshot */
  previous: Record<string, unknown> | null;
  /** Timestamp of optimistic add — doubles as "submission time" */
  addedAt: number;
  /** Auto-computed: which top-level field keys actually changed (for granular field locking) */
  changedFields: string[];
}

/** Granular lock result returned by isEntityLocked helper */
export interface GranularLock {
  /** Whether the entity itself is locked */
  isLocked: boolean;
  /** Which specific field keys are locked (empty = whole entity locked or not locked at all) */
  lockedFields: string[];
}

/** Compute which top-level keys differ between two plain objects */
export function computeChangedFields(
  proposed: Record<string, unknown>,
  previous: Record<string, unknown> | null,
): string[] {
  if (!previous) return Object.keys(proposed); // New entity — all fields are "changed"
  const changed: string[] = [];
  const allKeys = new Set([...Object.keys(proposed), ...Object.keys(previous)]);
  const skip = new Set(['id', 'sortOrder', 'categoryIds']);
  for (const key of allKeys) {
    if (skip.has(key)) continue;
    const a = proposed[key];
    const b = previous[key];
    // Deep equality via JSON stringify for objects/arrays
    if (typeof a === 'object' || typeof b === 'object') {
      if (JSON.stringify(a) !== JSON.stringify(b)) changed.push(key);
    } else if (String(a ?? '') !== String(b ?? '')) {
      changed.push(key);
    }
  }
  return changed;
}

// ── Singleton store ────────────────────────────────────────────────────────

let optimisticIds = new Set<string>();
let proposedChanges = new Map<string, ProposedChange>();
const listeners = new Set<() => void>();

function notify() { listeners.forEach(fn => fn()); }
function subscribe(cb: () => void) { listeners.add(cb); return () => { listeners.delete(cb); }; }

// Snapshots for useSyncExternalStore (must be referentially stable when unchanged)
let idsSnapshot = optimisticIds;
let changesSnapshot = proposedChanges;

function getIdsSnapshot() { return idsSnapshot; }
function getChangesSnapshot() { return changesSnapshot; }

// ── Mutations ──────────────────────────────────────────────────────────────

function addOptimistic(entityId: string, change: ProposedChange) {
  optimisticIds = new Set(optimisticIds);
  optimisticIds.add(entityId);
  idsSnapshot = optimisticIds;

  proposedChanges = new Map(proposedChanges);
  proposedChanges.set(entityId, change);
  changesSnapshot = proposedChanges;

  notify();
}

function removeOptimistic(entityId: string) {
  if (!optimisticIds.has(entityId)) return;
  optimisticIds = new Set(optimisticIds);
  optimisticIds.delete(entityId);
  idsSnapshot = optimisticIds;

  proposedChanges = new Map(proposedChanges);
  proposedChanges.delete(entityId);
  changesSnapshot = proposedChanges;

  notify();
}

/** Bulk-clear all optimistic IDs that are now confirmed by the server pendingEntities */
function reconcile(serverEntityIds: Set<string>) {
  let changed = false;
  const nextIds = new Set(optimisticIds);
  const nextChanges = new Map(proposedChanges);

  // Remove optimistic entries that the server has already incorporated
  for (const id of optimisticIds) {
    if (serverEntityIds.has(id)) {
      // Server confirmed — keep the proposed change for card preview, remove from optimistic-only set
      // Actually no — once the server confirms it, the pendingEntityIds from profile.pendingEntities
      // will handle locking. So remove from optimistic set.
      nextIds.delete(id);
      changed = true;
    }
  }

  // Also remove any proposed changes for entities the server no longer lists as pending
  // (i.e. they were approved/rejected)
  for (const id of proposedChanges.keys()) {
    if (!serverEntityIds.has(id) && !nextIds.has(id)) {
      nextChanges.delete(id);
      changed = true;
    }
  }

  if (changed) {
    optimisticIds = nextIds;
    idsSnapshot = nextIds;
    proposedChanges = nextChanges;
    changesSnapshot = nextChanges;
    notify();
  }
}

function clearAll() {
  optimisticIds = new Set();
  idsSnapshot = optimisticIds;
  proposedChanges = new Map();
  changesSnapshot = proposedChanges;
  notify();
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function usePendingChanges() {
  const ids = useSyncExternalStore(subscribe, getIdsSnapshot, getIdsSnapshot);
  const changes = useSyncExternalStore(subscribe, getChangesSnapshot, getChangesSnapshot);

  const addPending = useCallback((entityId: string, change: Omit<ProposedChange, 'addedAt' | 'changedFields'>) => {
    const changedFields = computeChangedFields(change.proposed, change.previous);
    addOptimistic(entityId, { ...change, addedAt: Date.now(), changedFields });
  }, []);

  const removePending = useCallback((entityId: string) => {
    removeOptimistic(entityId);
  }, []);

  const hasPending = useCallback((entityId: string) => {
    return ids.has(entityId);
  }, [ids]);

  const getProposed = useCallback((entityId: string): ProposedChange | undefined => {
    return changes.get(entityId);
  }, [changes]);

  return {
    /** Currently-optimistic entity IDs (not yet confirmed by server) */
    optimisticIds: ids,
    /** Proposed change snapshots keyed by entityId */
    proposedChanges: changes,
    /** Add an optimistic lock + proposed change */
    addPending,
    /** Remove an optimistic lock (on API failure or rollback) */
    removePending,
    /** Check if an entity has an optimistic pending request */
    hasPending,
    /** Get the proposed change snapshot for before→after rendering */
    getProposed,
    /** Reconcile with server-confirmed pending entities */
    reconcile,
    /** Clear all optimistic state */
    clearAll,
  };
}
